import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui';
import { SubscriptionStatusBadge } from './subscription-status-badge';
import { SubscriptionDetailCard } from './subscription-detail-card';
import { formatDate, formatCents, truncate } from '@/lib/utils';
import { MerchantCell } from '@/components/shared/merchant-cell';
import type { SortState } from '@/hooks/use-sort-state';
import type { AdminSubscription } from '@/types/admin-subscription';

/** Sortierbare Spalten der Subscription-Tabelle */
export type SubscriptionSortColumn = 'createdAt' | 'status' | 'grossAmount' | 'chargeCount';

interface SubscriptionTableProps {
  subscriptions: AdminSubscription[];
  expandedId: string | null;
  onToggleExpand: (subscriptionId: string) => void;
  onTerminate: (subscriptionId: string) => void;
  isTerminating: boolean;
  sort?: SortState<SubscriptionSortColumn>;
  onToggleSort?: (column: SubscriptionSortColumn) => void;
}

function formatInterval(interval: string, t: (key: string) => string): string {
  const key = `intervals.${interval}`;
  const translated = t(key);
  return translated === key ? interval : translated;
}

export function SubscriptionTable({
  subscriptions,
  expandedId,
  onToggleExpand,
  onTerminate,
  isTerminating,
  sort,
  onToggleSort,
}: SubscriptionTableProps) {
  const { t } = useTranslation('subscriptions');

  if (subscriptions.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        {t('list.empty')}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('list.columns.subscriptionId')}</TableHead>
          <TableHead>{t('list.columns.merchant')}</TableHead>
          <TableHead>{t('list.columns.service')}</TableHead>
          <SortableHead
            label={t('list.columns.status')}
            column="status"
            sort={sort}
            onToggle={onToggleSort}
          />
          <TableHead>{t('list.columns.operator')}</TableHead>
          <TableHead>{t('list.columns.interval')}</TableHead>
          <SortableHead
            label={t('list.columns.amount')}
            column="grossAmount"
            sort={sort}
            onToggle={onToggleSort}
          />
          <SortableHead
            label={t('list.columns.charges')}
            column="chargeCount"
            sort={sort}
            onToggle={onToggleSort}
          />
          <SortableHead
            label={t('list.columns.created')}
            column="createdAt"
            sort={sort}
            onToggle={onToggleSort}
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {subscriptions.map((sub) => (
          <Fragment key={sub.subscriptionId}>
            <TableRow
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onToggleExpand(sub.subscriptionId)}
            >
              <TableCell className="font-mono text-sm">
                {truncate(sub.subscriptionId, 16)}
              </TableCell>
              <TableCell><MerchantCell merchantId={sub.merchantId} /></TableCell>
              <TableCell>
                <Link
                  to={`/services/${sub.serviceId}`}
                  className="font-mono text-sm text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {sub.serviceId}
                </Link>
              </TableCell>
              <TableCell>
                <SubscriptionStatusBadge status={sub.status} />
              </TableCell>
              <TableCell>{sub.operatorId ?? '-'}</TableCell>
              <TableCell>{formatInterval(sub.subscriptionInterval, t)}</TableCell>
              <TableCell>{formatCents(sub.grossAmount)}</TableCell>
              <TableCell>{sub.chargeCount}</TableCell>
              <TableCell>{formatDate(sub.createdAt)}</TableCell>
            </TableRow>
            {expandedId === sub.subscriptionId && (
              <TableRow>
                <TableCell colSpan={9} className="p-0">
                  <SubscriptionDetailCard
                    subscription={sub}
                    onTerminate={() => onTerminate(sub.subscriptionId)}
                    isTerminating={isTerminating}
                  />
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

/** Sortierbare Spaltenkoepfe mit Pfeil-Indikator */
function SortableHead<T extends string>({
  label,
  column,
  sort,
  onToggle,
}: {
  label: string;
  column: T;
  sort?: SortState<T>;
  onToggle?: (column: T) => void;
}) {
  const isActive = sort?.column === column;

  return (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/50"
      onClick={() => onToggle?.(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          sort.direction === 'asc'
            ? <ChevronUpIcon className="h-3.5 w-3.5" />
            : <ChevronDownIcon className="h-3.5 w-3.5" />
        )}
      </span>
    </TableHead>
  );
}
